import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  countersSelector,
  dateEndSelector,
  dateInitSelector,
  distributionsSelector,
  errorCodeSelector,
  graphTypeSelector,
  graphTypesSelector,
  gridSelector,
  metricTypeSelector,
  metricTypesSelector,
  overviewSelector,
  productsSelector,
} from './store/selector';
import {
  getMetricsFailureApiAction,
  getMetricsInitApiAction,
  getMetricsSuccessApiAction,
  metricsClearAction,
  setEndDateAction,
  setInitDateAction,
  getMetricTypesInitApiAction,
  getMetricTypesSuccessApiAction,
  getMetricTypesFailureApiAction,
  postMetricTypeInitApiAction,
  postMetricTypeSuccessApiAction,
  postMetricTypeFailuireApiAction,
  putMetricTypeInitApiAction,
  putMetricTypeSuccessApiAction,
  putMetricTypeFailuireApiAction,
  deleteMetricTypeInitApiAction,
  deleteMetricTypeSuccessApiAction,
  deleteMetricTypeFailuireApiAction,
  setMetricTypeAction,
  postMetricsInitApiAction,
  postMetricsSuccessApiAction,
  postMetricsFailureApiAction,
  setStoreAction,
  setGraphTypeAction,
  getGraphTypesInitApiAction,
  getGraphTypesSuccessApiAction,
  getGraphTypesFailureApiAction,
  putGraphTypeFailuireApiAction,
  putGraphTypeInitApiAction,
  putGraphTypeSuccessApiAction,
} from './store/reducer';
import { metricsApiService } from './services/api';
import { Moment } from 'moment';
import { GetMetricsParams } from './interfaces/get-metrics';
import { PostMetricTypePayloadRequest } from './interfaces/post-metric-type';
import { PutMetricTypePayloadRequest } from './interfaces/put-metric-type';
import { MetricType } from '../../interfaces/metricType.interface';
import { PostMetricsPayloadRequest } from './interfaces/post-metrics';
import { storeSelector } from './store/selector';
import { GraphType } from '../../interfaces/graphType.interface';
import { PutGraphTypePayloadRequest } from './interfaces/put-graph-type';

export function useMetricsCore() {
  const metricTypes = useSelector(metricTypesSelector);
  const metricType = useSelector(metricTypeSelector);
  const graphTypes = useSelector(graphTypesSelector);
  const graphType = useSelector(graphTypeSelector);
  const grid = useSelector(gridSelector);
  const overview = useSelector(overviewSelector);
  const counters = useSelector(countersSelector);
  const distributions = useSelector(distributionsSelector);
  const products = useSelector(productsSelector);
  const dateInit = useSelector(dateInitSelector);
  const dateEnd = useSelector(dateEndSelector);
  const store = useSelector(storeSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getMetrics = useCallback(
    async (
      id: string,
      params: GetMetricsParams,
      onSuccess?: () => void,
      onError?: () => void
    ) => {
      try {
        dispatch(getMetricsInitApiAction());
        const response = await metricsApiService.getMetrics(id, params);
        console.log(response)
        dispatch(getMetricsSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onError?.();
        dispatch(
          getMetricsFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getMetricTypes = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getMetricTypesInitApiAction());
        const response = await metricsApiService.getMetricTypes(id);
        dispatch(getMetricTypesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getMetricTypesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const getGraphTypes = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getGraphTypesInitApiAction());
        const response = await metricsApiService.getGraphTypes(id);
        dispatch(getGraphTypesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getGraphTypesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postMetricType = useCallback(
    async (
      id: string,
      body: PostMetricTypePayloadRequest,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postMetricTypeInitApiAction());
        await metricsApiService.postMetricType(id, body);
        dispatch(postMetricTypeSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postMetricTypeFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putMetricType = useCallback(
    async (
      id: string,
      mid: string,
      body: PutMetricTypePayloadRequest,
      onSuccess?: () => void,
      onFailure?: (error: any) => void
    ) => {
      try {
        dispatch(putMetricTypeInitApiAction());
        await metricsApiService.putMetricType(id, mid, body);
        dispatch(putMetricTypeSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        onFailure?.(error);
        dispatch(putMetricTypeFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putGraphType = useCallback(
    async (
      id: string,
      mid: string,
      body: PutGraphTypePayloadRequest,
      onSuccess?: () => void
    ) => {
      try {
        dispatch(putGraphTypeInitApiAction());
        await metricsApiService.putGraphType(id, mid, body);
        dispatch(putGraphTypeSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(putGraphTypeFailuireApiAction());
      }
    },
    [dispatch]
  );

  const deleteMetricType = useCallback(
    async (id: string, mid: string, onSuccess?: () => void) => {
      try {
        dispatch(deleteMetricTypeInitApiAction());
        await metricsApiService.deleteMetricType(id, mid);
        dispatch(deleteMetricTypeSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(deleteMetricTypeFailuireApiAction());
      }
    },
    [dispatch]
  );

  const postMetrics = useCallback(
    async (
      id: string,
      body: PostMetricsPayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void,
    ) => {
      try {
        dispatch(postMetricsInitApiAction());
        await metricsApiService.postMetrics(id, body);
        dispatch(postMetricsSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(postMetricsFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const setMetricType = useCallback((metric: MetricType | null) => {
    dispatch(setMetricTypeAction(metric));
  }, [dispatch]);

  const setGraphType = useCallback((graph: GraphType | null) => {
    dispatch(setGraphTypeAction(graph));
  }, [dispatch]);

  const setInitDate = useCallback((date: Moment | null) => {
    dispatch(setInitDateAction(date));
  }, [dispatch]);

  const setEndDate = useCallback((date: Moment | null) => {
    dispatch(setEndDateAction(date));
  }, [dispatch]);

  const setStore = useCallback((store: string) => {
    dispatch(setStoreAction(store));
  }, [dispatch]);

  const clearMetrics = useCallback(() => {
    dispatch(metricsClearAction());
  }, [dispatch]);

  const getDailySummary = useCallback(
    async (
      companyId: string,
      integrationPrefix: string,
      startDate: string,
      endDate: string
    ) => {
      try {
        const response = await metricsApiService.getDailySummary(
          companyId,
          integrationPrefix,
          startDate,
          endDate
        );
        return response;
      } catch (error: any) {
        console.error('Error fetching daily summary:', error);
        throw error;
      }
    },
    []
  );

  return {
    metricTypes,
    metricType,
    graphTypes,
    graphType,
    grid,
    overview,
    counters,
    distributions,
    products,
    dateInit,
    dateEnd,
    store,
    errorCode,
    getMetricTypes,
    getGraphTypes,
    postMetricType,
    putMetricType,
    putGraphType,
    deleteMetricType,
    getMetrics,
    postMetrics,
    setMetricType,
    setGraphType,
    setInitDate,
    setEndDate,
    setStore,
    clearMetrics,
    getDailySummary,
  };
}
